import {
  AccountTree,
  Add,
  ArrowBack,
  AttachMoney,
  Chat,
  ChevronLeft,
  ChevronRight,
  Close,
  ExpandMore,
  Folder,
  InsertDriveFile,
  KeyboardArrowLeft,
  LabelImportant,
  MoreHoriz,
  PlaylistAddCheck,
  ShoppingCart,
  Tune,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  // List,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { EndpointUrl } from 'api/endpoint';
import { useEntity, useEntityActions, useEntityChildren } from 'api/entity';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useCreateEntityModal } from 'components/createEntity';
import { useCreateFieldModal } from 'components/createField';
import EditableTextField from 'components/editableTextField';
import { EntityDescription } from 'components/EntityDescription';
import EntityMenu from 'components/entityMenu';
import EntityComponent from 'components/entityView';
import { ProjectsList } from 'components/ProjectsList';
import { FinsightEntityFieldValue } from 'components/renderField';
import DOMPurify from 'dompurify';
import Handlebars from 'handlebars';
import useIsMobile from 'hooks/useIsMobile';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ComputedFieldMeta, FinsightEntity, FinsightEntityField } from 'shared';
import { formatName, plural, sortEntities, sortFields } from 'utils/fields';

import EntityChildrenFilter from './components/ChildrenFilter';

export type ViewType =
  | 'list'
  | 'card'
  | 'table'
  | 'text'
  | 'spreadsheet'
  | 'discussion'
  | 'files'
  | 'flashcards';

import DataMaidEntityButton from './components/DataMaid';
import { EntityDiscussion } from './components/EntityDiscussion';
import { EntityFiles } from './components/EntityFiles';
import FlashcardsView from './flashcardsView';
import SpreadsheetView from './spreadsheetView';
import TextView from './textView';
import { FinsightEntityPageProps } from './types';

export function View(props: FinsightEntityPageProps) {
  const { entityId: overrideEntityId } = props;
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    isLoading,
    error,
    data: entity,
  } = useEntity(overrideEntityId ?? params.id);
  const { updateEntity } = useEntityActions();

  const [allChildren, setAllChildren] = useState(false);

  const { data: _children, mutate: mutateChildren } = useEntityChildren(
    overrideEntityId ?? params.id,
    allChildren,
  );

  const { children, discussionCount, filesCount, configurationEntity } =
    useMemo(() => {
      const children = [];
      let discussionCount = 0;
      let filesCount = 0;
      let configurationEntity: FinsightEntity | undefined;

      for (const child of _children || []) {
        if (child.types[0] === 'discussion') {
          discussionCount++;
          continue;
        }
        if (child.types[0] === 'blackframe.file') {
          filesCount++;
          continue;
        }
        if (child.types[0] === 'blackframe.internal.ui.configuration') {
          configurationEntity = child;
          continue;
        }
        children.push(child);
      }
      return { children, discussionCount, filesCount, configurationEntity };
    }, [_children]);
  const workspaceId = useWorkspaceId();

  const { showCreateEntityModal } = useCreateEntityModal();
  const { showCreateFieldModal } = useCreateFieldModal();
  const navigate = useNavigate();

  const isMobile = useIsMobile();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] =
    useState<null | HTMLElement>(null);

  const [sidebarOpen, setSidebarOpen] = useState(true);

  const childrenByType = useMemo(() => {
    if (!children?.length) return {};

    const result = children.reduce(
      (acc, c) => ((acc[c.types[0]] = [...(acc[c.types[0]] || []), c]), acc),
      {} as Record<string, FinsightEntity[]>,
    );

    // Add an "all" entry that contains all children
    result.all = children;
    // Add a "smart" entry that contains all children (same as "all" for now)
    result.smart = children;

    for (const key in result) {
      result[key] = Array.from(result[key]).sort(sortEntities);
    }

    return result;
  }, [children]);

  const childrenTypes = useMemo(() => {
    // Filter out "all" and "smart" from the types
    const types = Object.keys(childrenByType || {}).filter(
      (type) => type !== 'all' && type !== 'smart',
    );

    // Sort the rest of the types
    types.sort((a, b) => {
      if (a === 'project' && b !== 'project') return -1;
      if (a !== 'project' && b === 'project') return 1;

      if (a === 'task' && b !== 'task') return -1;
      if (a !== 'task' && b === 'task') return 1;

      return a.localeCompare(b);
    });

    // Add "smart" at the beginning if there are children
    if (childrenByType?.smart) {
      types.unshift('smart');
    }
    // Add "all" after "smart" if there are children
    if (childrenByType?.all) {
      types.push('all');
    }

    return types;
  }, [childrenByType]);

  const [viewType, setViewType] = useState<ViewType>('list');
  const [categorizeBy, setCategorizeBy] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string | null>(null);

  // Initialize viewType from configuration entity when entity or configuration changes
  useEffect(() => {
    if (
      configurationEntity?.extraFields?.viewMode &&
      typeof configurationEntity.extraFields.viewMode === 'string'
    ) {
      const configuredViewMode = configurationEntity.extraFields
        .viewMode as ViewType;
      setViewType(configuredViewMode);
    }
  }, [configurationEntity]);

  const tab = searchParams.get('tab');
  const setTab = useCallback(
    (v: string) => {
      setSearchParams((prev) => (prev.set('tab', v), prev), {
        replace: true,
      });
      if (v) {
        setViewType('list');
      }
    },
    [setSearchParams],
  );

  const seperateTabs = isMobile || childrenTypes.length > 3;

  const [entityStore, setEntityStore] = useState<
    Record<string, FinsightEntity>
  >({});

  useEffect(() => {
    if (
      entity?.types[0] &&
      (!tab || !childrenTypes.includes(tab)) &&
      viewType !== 'discussion' &&
      viewType !== 'files'
    ) {
      setTab(
        childrenTypes.length
          ? childrenTypes.filter((x) => x !== 'smart')[0]
          : entity?.types[0],
      );
    }
  }, [childrenTypes, entity?.types, setTab, tab, viewType]);

  const entityFieldPaths = useMemo(() => {
    if (!entity) return null;
    const fields = Object.values(entity.extraFieldsMeta || {});
    if (!fields.length) return null;
    return findEntityTypes(fields, entity.extraFields).filter((x) => x.id);
  }, [entity]);

  useEffect(() => {
    if (entityFieldPaths?.length) {
      fetch(`${EndpointUrl}/workspace/${workspaceId}/entities/all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-finsight-token': localStorage.getItem('token') || '',
        },
        body: JSON.stringify([
          {
            name: 'field_query_ids',
            property: 'id',
            operator: 'in',
            value: entityFieldPaths.map(({ id }) => id).flat(),
          },
        ]),
      }).then(async (res) => {
        const data = (await res.json()) as FinsightEntity[];
        const store: Record<string, FinsightEntity> = {};
        for (const entity of data) {
          store[entity.id] = entity;
        }
        setEntityStore(store);
      });
    }
  }, [entityFieldPaths, workspaceId]);

  const entityWithResolvedExtraFields = useMemo(() => {
    const entityCopy = structuredClone(entity);
    if (!entityCopy) return null;
    if (!entityFieldPaths) return entityCopy;

    for (const { paths, id } of entityFieldPaths) {
      _.set(entityCopy.extraFields, paths, entityStore[id]);
    }

    return entityCopy;
  }, [entity, entityFieldPaths, entityStore]);

  const theme = useTheme();

  const childrenCreateTypes = childrenTypes.filter(
    (x) =>
      !(
        x === 'project' ||
        x === 'task' ||
        x === 'commodity' ||
        x === 'transaction' ||
        x === 'group' ||
        x === 'item' ||
        x === 'all' ||
        x === 'smart'
      ),
  );

  if (isLoading)
    return (
      <Stack direction="row" pt={5} gap={3}>
        <Stack flexGrow={1}>
          <Skeleton height={90} />
          <Skeleton height={200} />
          <Skeleton height={50} />
          <Skeleton height={50} />
          <Skeleton height={50} />
        </Stack>
        <Stack flex="0 0 250px">
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
        </Stack>
      </Stack>
    );

  if (error || !entity)
    return (
      <Stack alignItems="center" justifyContent="center" pt={10} gap={4}>
        <Box>
          <Typography variant="h1" fontWeight={800} color="primary">
            404
          </Typography>
        </Box>
        <Box>
          <Typography variant="h4">Entity not found</Typography>
        </Box>
        <Stack direction="row" gap={2}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
            Go Back
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/workspace/${workspaceId}/groups`)}
          >
            Go to Groups
          </Button>
        </Stack>
      </Stack>
    );

  const templateVars: Record<string, any> = {};

  Handlebars.registerHelper('math', function (operator, lvalue, rvalue) {
    lvalue = parseFloat(lvalue);
    rvalue = parseFloat(rvalue);

    switch (operator) {
      case 'add':
        return lvalue + rvalue;
      case 'sub':
        return lvalue - rvalue;
      case 'mult':
        return lvalue * rvalue;
      case 'div':
        return lvalue / rvalue;
      case 'mod':
        return lvalue % rvalue;
    }

    return NaN;
  });

  Handlebars.registerHelper('getVar', function (name) {
    return templateVars[name];
  });

  Handlebars.registerHelper('setVar', function (name, value) {
    templateVars[name] = value;
  });

  Handlebars.registerHelper('style', function (...args) {
    if (args.length < 2) return '';
    const options = args.pop();
    const inline = options?.hash?.inline || true;
    let styleString = '';

    for (const name of args) {
      const key = `${name}`.toLowerCase();
      const premadeStyles: Record<string, string> = {
        'flex-v': 'display:flex;flex-direction:column;',
        'flex-h': 'display:flex;flex-direction:row;',
        'j-spaced': 'justify-content:space-between;',
        'a-center': 'align-items:center;',
        h1: `font-size:${theme.typography.h1.fontSize};font-weight:${theme.typography.h1.fontWeight};`,
        h2: `font-size:${theme.typography.h2.fontSize};font-weight:${theme.typography.h2.fontWeight};`,
        h3: `font-size:${theme.typography.h3.fontSize};font-weight:${theme.typography.h3.fontWeight};`,
        h4: `font-size:${theme.typography.h4.fontSize};font-weight:${theme.typography.h4.fontWeight};`,
        h5: `font-size:${theme.typography.h5.fontSize};font-weight:${theme.typography.h5.fontWeight};`,
        h6: `font-size:${theme.typography.h6.fontSize};font-weight:${theme.typography.h6.fontWeight};`,
        'text.secondary': `color:${theme.palette.text.secondary};`,
      };

      styleString += premadeStyles[key] || '';
    }

    for (const key in options.hash) {
      styleString += `${key}:${options.hash[key]};`;
    }

    return inline ? `style="${styleString}"` : styleString;
  });

  Handlebars.registerHelper('round', function (value, decimals) {
    if (isNaN(+value)) return value;
    if (isNaN(decimals)) decimals = 0;

    return Number(value).toFixed(decimals);
  });

  const hbr = `
    <div style="display:flex;flex-direction:column;">
      <span {{{style "h6"}}}>Summary</span>
      <span {{{style "h4"}}}>{{ name }}</span>
      <div {{{style "flex-h" "j-spaced"}}}>
        <div {{{style "flex-v"}}}>
          <span>Bill To:</span>
          <span {{{style "h6"}}}>{{extraFields.buyer}}</span>
        </div>
        <div {{{style "flex-v"}}}>
          <span>Pay To:</span>
          <span {{{style "h6"}}}>{{extraFields.seller}}</span>
        </div>
      </div>
      <hr />
      <span>Items</span>
      <div>
      {{setVar "subtotal" 0}}
      {{#each extraFields.commodities}}
        <div {{{style "flex-h" "j-spaced" "a-center"}}}>
          <div {{{style "flex-h" gap="16px"}}}>
            <img src="{{this.commodity.extraFields.image_url}}" alt="commodity" width="48" height="48" />
            <div {{{style "flex-v"}}}>
              <span>
                {{this.commodity.name}}
              </span>
              <span {{{style "text.secondary"}}}>
                {{this.commodity.extraFields.brand}}, \${{this.commodity.extraFields.price_per_unit}} /{{this.commodity.extraFields.unit}}
              </span>
            </div>
          </div>
          <div {{{style "flex-h" "a-center" gap="8px"}}}>
            {{setVar "calc_price" (math "mult" this.commodity.extraFields.price_per_unit this.quantity)}}
            {{setVar "subtotal" (math "add" (getVar "subtotal") (getVar "calc_price"))}}
            <span>{{this.quantity}}x</span>
            <span {{{style "h6"}}}>\${{round (getVar "calc_price") 2}}</span>
          </div>
        </div>
        <hr />
      {{/each}}
      </div>
      <div {{{style "flex-h" "j-spaced" "h6"}}}>
        <span>Subtotal:</span>
        <span>\${{round (getVar "subtotal") 2}}</span>
      </div>
      <div {{{style "flex-h" "j-spaced" "h6"}}}>
        <span>Other Costs:</span>
        <span>\${{extraFields.other_costs}}</span>
      </div>
      <div {{{style "flex-h" "j-spaced" "h6"}}}>
        <span>Adjustments:</span>
        <span>\${{extraFields.adjustments}}</span>
      </div>
      <div {{{style "flex-h" "j-spaced" "h5"}}}>
        <span>Calculated Total:</span>
        <span>\${{round (math "add" (math "add" extraFields.other_costs (getVar "subtotal")) extraFields.adjustments) 2}}</span>
      </div>
      <div {{{style "flex-h" "j-spaced" "h4"}}}>
        <span>Total:</span>
        <span>\${{extraFields.amount}}</span>
      </div>
    </div>
  `;

  const template = Handlebars.compile(hbr);

  const html = template(entityWithResolvedExtraFields);

  const escapedHtml = DOMPurify.sanitize(html);

  return (
    <>
      {isMobile && (
        <Stack direction="row">
          <Button
            onClick={() => navigate(-1)}
            startIcon={<KeyboardArrowLeft />}
          >
            Back
          </Button>
        </Stack>
      )}

      <Stack mt={2} direction="row" justifyContent="space-between">
        <Stack gap={2} flexGrow={1} minWidth={0}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" gap={1}>
              <Chip
                variant="outlined"
                size="small"
                label={formatName(entity.types[0])}
              />
              {entity.parents?.length && (
                <Breadcrumbs>
                  {Array.from(entity.parents)
                    .reverse()
                    .map((parent) => (
                      <EntityComponent
                        key={parent.id}
                        entity={parent}
                        viewType="text"
                      />
                    ))}
                </Breadcrumbs>
              )}
            </Stack>

            <Stack direction="row" gap={1} alignItems="center">
              <Button
                startIcon={<Add />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                New
              </Button>
              <IconButton
                onClick={(e) => setMoreOptionsAnchorEl(e.currentTarget)}
              >
                <MoreHoriz />
              </IconButton>
              <DataMaidEntityButton entity={entity} />
            </Stack>
          </Stack>
          <EditableTextField
            fullWidth
            variant="minimal"
            value={entity.name ?? ''}
            onChange={async (v) => {
              await updateEntity(entity.id, { name: v });
            }}
            InputProps={{
              sx: {
                fontSize: 'h4.fontSize',
                lineHeight: '1em',
              },
            }}
          />

          <EntityDescription entity={entity} />
          <Stack gap={2}>
            {sortFields(Object.values(entity.extraFieldsMeta)).map((field) => (
              <FinsightEntityFieldValue
                viewType="entity_main"
                editable
                field={field}
                key={field.name}
                value={entity.extraFields[field.name]}
                onChange={async (v) => {
                  await updateEntity(entity.id, {
                    extraFields: { [field.name]: v },
                  });
                }}
              />
            ))}
          </Stack>
          {isMobile && (
            <Accordion variant="outlined">
              <AccordionSummary expandIcon={<ExpandMore />}>
                More Fields
              </AccordionSummary>
              <AccordionDetails>
                <Stack gap={2}>
                  {sortFields(Object.values(entity.extraFieldsMeta)).map(
                    (field) => (
                      <FinsightEntityFieldValue
                        viewType="entity_sidebar"
                        editable
                        field={field}
                        key={field.name}
                        value={entity.extraFields[field.name]}
                        onChange={async (v) => {
                          await updateEntity(entity.id, {
                            extraFields: { [field.name]: v },
                          });
                        }}
                      />
                    ),
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
          <Stack
            gap={1}
            alignItems={seperateTabs ? 'stretch' : 'center'}
            direction={seperateTabs ? 'column' : 'row'}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              scrollButtons="auto"
              allowScrollButtonsMobile
              variant="scrollable"
              sx={{
                flexGrow: 1,
                '.MuiTabs-flexContainer': {
                  width: 'fit-content',
                },
              }}
            >
              {childrenTypes.map((type) => (
                <Tab
                  key={type}
                  value={type}
                  label={
                    type === 'smart'
                      ? 'Smart'
                      : type === 'all'
                        ? 'All'
                        : plural(formatName(type))
                  }
                />
              ))}
            </Tabs>
            <Stack
              justifyContent={seperateTabs ? 'flex-end' : 'center'}
              direction="row"
              gap={1}
              alignItems="center"
            >
              {seperateTabs && tab && childrenByType && (
                <Button
                  sx={{ flexShrink: 0 }}
                  startIcon={<Add />}
                  onClick={() => showCreateEntityModal(tab, entity)}
                >
                  New{' '}
                  {formatName(
                    tab === 'all' || tab === 'smart' ? entity.types[0] : tab,
                  )}
                </Button>
              )}
              {seperateTabs && <Stack flexGrow={1} />}
              <EntityChildrenFilter
                childrenAll={{
                  current: allChildren,
                  onChange: setAllChildren,
                }}
                viewType={{
                  current: viewType,
                  onChange: (value) => {
                    const newViewType =
                      typeof value === 'function' ? value(viewType) : value;
                    if (
                      newViewType === 'discussion' ||
                      newViewType === 'files'
                    ) {
                      setTab('');
                    }
                    setViewType(value);
                  },
                }}
                categorizeBy={{
                  current: categorizeBy,
                  onChange: setCategorizeBy,
                }}
                sortBy={{
                  current: sortBy,
                  onChange: setSortBy,
                }}
                tab={{
                  current: tab,
                  onChange: setTab,
                }}
                childrenTypes={childrenTypes}
                extraFieldNames={
                  tab
                    ? Array.from(
                        new Set(
                          childrenByType[tab]
                            ?.map((child) =>
                              Object.keys(child.extraFieldsMeta || {}),
                            )
                            .flat(),
                        ),
                      )
                    : []
                }
                entity={entity}
              />

              <Tooltip title="Discussion">
                <Badge badgeContent={discussionCount} color="primary">
                  <ToggleButton
                    value="discussion"
                    size="small"
                    selected={viewType === 'discussion'}
                    onClick={() => {
                      if (viewType === 'discussion') {
                        setViewType('list');
                        return;
                      }
                      setTab('');
                      setViewType('discussion');
                    }}
                  >
                    {viewType === 'discussion' ? <Close /> : <Chat />}
                  </ToggleButton>
                </Badge>
              </Tooltip>
              <Tooltip title="Files">
                <Badge badgeContent={filesCount} color="primary">
                  <ToggleButton
                    value="files"
                    size="small"
                    selected={viewType === 'files'}
                    onClick={() => {
                      if (viewType === 'files') {
                        setViewType('list');
                        return;
                      }
                      setTab('');
                      setViewType('files');
                    }}
                  >
                    {viewType === 'files' ? <Close /> : <InsertDriveFile />}
                  </ToggleButton>
                </Badge>
              </Tooltip>
            </Stack>
          </Stack>
          {!seperateTabs && tab && childrenByType && (
            <Button
              startIcon={<Add />}
              onClick={() => showCreateEntityModal(tab, entity)}
            >
              New{' '}
              {formatName(
                tab === 'all' || tab === 'smart' ? entity.types[0] : tab,
              )}
            </Button>
          )}
          {viewType === 'discussion' && <EntityDiscussion entity={entity} />}
          {viewType === 'files' && <EntityFiles entity={entity} />}
          {tab &&
            childrenByType &&
            viewType !== 'discussion' &&
            viewType !== 'files' &&
            (viewType === 'card' ? (
              <Stack
                gap={2}
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(220px, 1fr))"
                sx={{
                  '& > *': {
                    aspectRatio: '1 / 1',
                  },
                }}
              >
                {childrenByType[tab]?.map((child) => (
                  <EntityComponent
                    parentControlledCardSize
                    hideType
                    key={child.id}
                    entity={child}
                    viewType="card"
                    hideParent={!allChildren}
                  />
                ))}
              </Stack>
            ) : viewType === 'table' ? (
              <>
                <DataGridPremium
                  rows={Array.from(childrenByType[tab]).sort(sortEntities)}
                  rowHeight={64}
                  columns={[
                    {
                      field: 'name',
                      headerName: 'Name',
                      minWidth: 200,
                    },
                    ...sortFields(
                      Object.values(childrenByType[tab][0]?.extraFieldsMeta),
                    ).map<GridColDef<FinsightEntity>>((field) => ({
                      field: field.name,
                      headerName: formatName(field.name),
                      minWidth: 200,
                      type:
                        field.schema.type !== 'object' &&
                        field.schema.type !== 'enum' &&
                        field.schema.type !== 'entity' &&
                        field.schema.type !== 'image' &&
                        field.schema.type !== 'file' &&
                        field.schema.type !== 'computed'
                          ? field.schema.type
                          : field.schema.type === 'computed'
                            ? (
                                field.schema.meta as
                                  | ComputedFieldMeta
                                  | undefined
                              )?.type
                            : undefined,
                      valueGetter: (_, row) => row.extraFields?.[field.name],
                      renderCell: (params) =>
                        params.row.extraFields ? (
                          <FinsightEntityFieldValue
                            editable
                            field={field}
                            value={params.row.extraFields[field.name]}
                            viewType="table"
                            source="table"
                            onChange={async (v) => {
                              await updateEntity(params.row.id, {
                                extraFields: { [field.name]: v },
                              });
                              mutateChildren();
                            }}
                          />
                        ) : (
                          params.value
                        ),
                    })),
                  ]}
                  sx={{
                    '.MuiDataGrid-cell > *': {
                      // flexGrow: 1,
                      width: '100%',
                    },
                    '.MuiDataGrid-cell:has(.MuiBadge-root)': {
                      padding: 0,
                    },
                  }}
                />
              </>
            ) : viewType === 'text' ? (
              <TextView entities={childrenByType[tab]} />
            ) : viewType === 'spreadsheet' ? (
              <SpreadsheetView entities={childrenByType[tab]} />
            ) : viewType === 'flashcards' ? (
              <FlashcardsView entities={childrenByType[tab]} />
            ) : (
              <ProjectsList
                hideType={tab !== 'all' && tab !== 'smart'}
                entities={childrenByType[tab]}
                entityViewProps={{
                  hideParent: !allChildren,
                }}
                categorizeBy={{
                  current: categorizeBy,
                  onChange: setCategorizeBy,
                }}
                sortBy={{
                  current: sortBy,
                  onChange: setSortBy,
                }}
              />
              // <List>
              //   {childrenByType[tab]?.map((child) => (
              //     <EntityComponent
              //       parentControlledCardSize
              //       hideType
              //       key={child.id}
              //       entity={child}
              //       viewType="list"
              //     />
              //   ))}
              // </List>
            ))}
          {entity.types[0] === 'transaction' && (
            <Box
              dangerouslySetInnerHTML={{ __html: escapedHtml }}
              sx={{
                '& *': {
                  borderColor: 'divider',
                },
                '& hr': {
                  borderTop: 0,
                },
              }}
            />
          )}
        </Stack>
        {!isMobile && (
          <Stack
            flexShrink={0}
            gap={1}
            width={sidebarOpen ? 250 : 48}
            overflow={!sidebarOpen ? 'hidden' : 'visible'}
            ml={sidebarOpen ? 4 : 0}
            sx={{
              transition: 'width 0.3s, margin 0.3s',
            }}
          >
            <IconButton
              sx={{
                alignSelf: 'flex-end',
              }}
              onClick={() => setSidebarOpen((prev) => !prev)}
            >
              {sidebarOpen ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
            <Stack
              width={250}
              gap={2}
              sx={{
                opacity: sidebarOpen ? 1 : 0,
                transition: 'opacity 0.3s ease',
                pointerEvents: sidebarOpen ? 'auto' : 'none',
                userSelect: sidebarOpen ? 'auto' : 'none',
              }}
            >
              {sortFields(Object.values(entity.extraFieldsMeta)).map(
                (field) => (
                  <FinsightEntityFieldValue
                    viewType="entity_sidebar"
                    editable
                    field={field}
                    key={field.name}
                    value={entity.extraFields[field.name]}
                    onChange={async (v) => {
                      await updateEntity(entity.id, {
                        extraFields: { [field.name]: v },
                      });
                    }}
                  />
                ),
              )}
              <Button
                startIcon={<Add />}
                onClick={() => showCreateFieldModal()}
              >
                Create New Field
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {childrenCreateTypes.map((type) => (
          <MenuItem
            key={type}
            onClick={() => showCreateEntityModal(type, entity)}
          >
            <ListItemIcon>
              <Add />
            </ListItemIcon>
            <ListItemText primary={`New ${formatName(type)}`} />
          </MenuItem>
        ))}
        {childrenCreateTypes.length > 0 && <Divider />}
        <MenuItem onClick={() => showCreateEntityModal('group', entity)}>
          <ListItemIcon>
            <Folder />
          </ListItemIcon>
          <ListItemText primary="New Group" />
        </MenuItem>
        <MenuItem onClick={() => showCreateEntityModal('item', entity)}>
          <ListItemIcon>
            <LabelImportant />
          </ListItemIcon>
          <ListItemText primary="New Item" />
        </MenuItem>
        <Divider />
        {entity.types[0] === 'project' && (
          <MenuItem onClick={() => showCreateEntityModal('project', entity)}>
            <ListItemIcon>
              <AccountTree />
            </ListItemIcon>
            <ListItemText primary="New Project" />
          </MenuItem>
        )}
        <MenuItem onClick={() => showCreateEntityModal('task', entity)}>
          <ListItemIcon>
            <PlaylistAddCheck />
          </ListItemIcon>
          <ListItemText primary="New Task" />
        </MenuItem>
        <MenuItem onClick={() => showCreateEntityModal('transaction', entity)}>
          <ListItemIcon>
            <AttachMoney />
          </ListItemIcon>
          <ListItemText primary="New Transaction" />
        </MenuItem>
        <MenuItem onClick={() => showCreateEntityModal('commodity', entity)}>
          <ListItemIcon>
            <ShoppingCart />
          </ListItemIcon>
          <ListItemText primary="New Commodity" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() =>
            showCreateEntityModal('', entity, {
              moreClicked: true,
            })
          }
        >
          <ListItemIcon>
            <Tune />
          </ListItemIcon>
          <ListItemText primary="More" />
        </MenuItem>
      </Menu>

      <EntityMenu
        anchorEl={moreOptionsAnchorEl}
        entity={entity}
        onClose={() => setMoreOptionsAnchorEl(null)}
        isDirectEntityPage
      />
    </>
  );
}

function findEntityTypes(
  fields: FinsightEntityField[],
  values: unknown,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  parentPaths: string[] = [],
) {
  const final: {
    paths: string[];
    id: string;
  }[] = [];
  for (const field of fields) {
    if (values === null || values === undefined || typeof values !== 'object')
      continue;

    const valuesMap = values as Record<string, unknown>;

    if (field.schema.multiple && !Array.isArray(valuesMap[field.name]))
      continue;
    if (field.schema.type !== 'entity' && field.schema.type !== 'object')
      continue;

    const isArray = Array.isArray(valuesMap[field.name]);

    const valuesArray = (
      isArray ? valuesMap[field.name] : [valuesMap[field.name]]
    ) as unknown[];

    for (const i in valuesArray) {
      const parentPath = [...parentPaths, field.name];
      if (isArray) parentPath.push(i);

      if (field.schema.type === 'entity') {
        final.push({
          paths: parentPath,
          id: valuesArray[i] as string,
        });
      }

      if (
        field.schema.type === 'object' &&
        Array.isArray(field.schema.properties)
      ) {
        final.push(
          ...findEntityTypes(
            field.schema.properties.map((x) => ({
              name: x.name ?? '',
              schema: x,
              type: '',
              workspace_id: 'global',
            })),
            valuesArray[i] as Record<string, unknown>,
            parentPath,
          ),
        );
      }
    }
  }

  return final;
}
